import { TranslateLoader } from '@ngx-translate/core';
import { merge } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as translationsDaDK from '../assets/da-DK.json';
import * as translationsDeAT from '../assets/de-AT.json';
import * as translationsDeCH from '../assets/de-CH.json';
import * as translationsDeDE from '../assets/de-DE.json';
import * as translationsDeIT from '../assets/de-IT.json';
import * as translationsDeLU from '../assets/de-LU.json';
import * as translationsElGR from '../assets/el-GR.json';
import * as translationsEnGB from '../assets/en-GB.json';
import * as translationsEnUS from '../assets/en-US.json';
import * as translationsEsES from '../assets/es-ES.json';
import * as translationsFiFI from '../assets/fi-FI.json';
import * as translationsFrBE from '../assets/fr-BE.json';
import * as translationsFrCH from '../assets/fr-CH.json';
import * as translationsFrFR from '../assets/fr-FR.json';
import * as translationsFrLU from '../assets/fr-LU.json';
import * as translationsItCH from '../assets/it-CH.json';
import * as translationsItIT from '../assets/it-IT.json';
import * as translationsNbNO from '../assets/nb-NO.json';
import * as translationsNlBE from '../assets/nl-BE.json';
import * as translationsNlNL from '../assets/nl-NL.json';
import * as translationsPtPT from '../assets/pt-PT.json';
import * as translationsSvSE from '../assets/sv-SE.json';

import { FALLBACK_LANGUAGE_LOCALE, SUPPORTED_LANGUAGE_LOCALES, SupportedLanguageLocale } from '../mmu-supported-languages';

/* eslint-enable @typescript-eslint/naming-convention */
export abstract class MmuTranslateFileLoaderBase implements TranslateLoader {

  protected readonly namespace?: string;

  protected constructor(public supportedLanguages = SUPPORTED_LANGUAGE_LOCALES) {
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  readonly libraryTranslations: Record<SupportedLanguageLocale, any> = {
    'en-US': translationsEnUS,
    'en-GB': translationsEnGB,
    'de-DE': translationsDeDE,
    'de-AT': translationsDeAT,
    'de-CH': translationsDeCH,
    'de-LU': translationsDeLU,
    'de-IT': translationsDeIT,
    'el-GR': translationsElGR,
    'fr-FR': translationsFrFR,
    'fr-CH': translationsFrCH,
    'fr-BE': translationsFrBE,
    'fr-LU': translationsFrLU,
    'it-CH': translationsItCH,
    'it-IT': translationsItIT,
    'nl-NL': translationsNlNL,
    'nl-BE': translationsNlBE,
    'fi-FI': translationsFiFI,
    'nb-NO': translationsNbNO,
    'da-DK': translationsDaDK,
    'sv-SE': translationsSvSE,
    'es-ES': translationsEsES,
    'pt-PT': translationsPtPT
  };

  getTranslation(locale: SupportedLanguageLocale): Observable<any> {
    if (!this.isLanguageSupported(locale)) {
      locale = FALLBACK_LANGUAGE_LOCALE;
    }

    return this.loadLanguageForLocale(locale).pipe(
      // If a namespace is defined, wrap the translations in the namespace
      map((res) => {
        if (this.namespace) {
          return { [this.namespace]: res };
        }
        return res;
      }),
      // Merge library translations with resolved translations
      map((res) => {
        const libTranslationsForLocale = this.libraryTranslations[locale];
        const translations = { ...libTranslationsForLocale?.default };
        return merge(translations, res);
      })
    );
  }

  isLanguageSupported(locale: string): boolean {
    if (!this.supportedLanguages) {
      return false;
    }
    return this.supportedLanguages.includes(locale as SupportedLanguageLocale);
  }

  protected abstract loadLanguageForLocale(locale: string): Observable<any>;
}
